import { useBrDetails } from '../../../../BrDetails';
import { BrNumeratorDetailsPopOverDataProps } from '../types';
import { addRecurringMonetaryAmounts, amountToRecurring } from '../../../../util';
import { BrDetails, Loan, MonetaryAmount, RecurringFrequency } from '../../../../generated/graphql';
import {
  getLoanExtraPayments,
  getLoanPayments,
  getLoanPaymentsAndExtraPayments,
  getLoansWithPaymentsOrExtraPayments,
  useLoans,
} from '../../../../Loan';

interface UseBrNumeratorDetailsPopOverDataArgs {
  householdID: string | undefined | null;
  onLoansWithDebtPaymentsFootnoteLoanClick: (loan: Loan) => void;
}

export interface UseBrNumeratorDetailsPopOverDataReturn {
  metaData: { isLoading: boolean };
  data: BrNumeratorDetailsPopOverDataProps;
}

interface GetBrNumeratorDetailsPopOverDisplayDataArgs {
  brDetails: BrDetails | undefined | null;
  loans: Loan[];
  onLoansWithDebtPaymentsFootnoteLoanClick: (loan: Loan) => void;
}

export function getBrDebtPaymentFootnoteItems(
  loans: Loan[],
  onLoansWithDebtPaymentsFootnoteLoanClick: (loan: Loan) => void
): BrNumeratorDetailsPopOverDataProps['footnoteItems'] {
  const loansWithDebtPayments = getLoansWithPaymentsOrExtraPayments(loans);

  return loansWithDebtPayments.map((loan) => {
    const frequency = getLoanPayments(loan)?.frequency ?? getLoanExtraPayments(loan)?.frequency;
    return {
      key: loan.id,
      title: loan.name,
      amount: getLoanPaymentsAndExtraPayments(loan, { frequency }),
      onClick: () => onLoansWithDebtPaymentsFootnoteLoanClick(loan),
    };
  });
}

export function getBrNumeratorDetailsPopOverDisplayData({
  brDetails,
  loans,
  onLoansWithDebtPaymentsFootnoteLoanClick,
}: GetBrNumeratorDetailsPopOverDisplayDataArgs): BrNumeratorDetailsPopOverDataProps {
  const loanDerivedAnnualDebtPayments = getLoanDerivedAnnualDebtPayments(loans);
  const backendAnnualDebtPayments = brDetails?.totalDebtPayments.annual;
  const totalAnnualDebtPaymentsMonetary = getGreaterMonetaryAmount(
    backendAnnualDebtPayments,
    loanDerivedAnnualDebtPayments?.amount
  );

  return {
    estimatedAnnualSpending: brDetails?.annualExpenses,
    totalAnnualDebtPaymentsMonetary,
    totalAnnualDebtPaymentsRecurring: amountToRecurring(totalAnnualDebtPaymentsMonetary, RecurringFrequency.Annually),
    estimatedAnnualSpendingSansDebtPayments: subtractDebtPaymentCorrection({
      estimatedAnnualSpending: brDetails?.annualExpenses,
      estimatedAnnualSpendingSansDebtPayments: brDetails?.estimatedSpendingMinusDebtPayments.annual,
      backendAnnualDebtPayments,
      totalAnnualDebtPayments: totalAnnualDebtPaymentsMonetary,
    }),
    footnoteItems: getBrDebtPaymentFootnoteItems(loans, onLoansWithDebtPaymentsFootnoteLoanClick),
  };
}

export function getBrScoreFromAnnualAmounts({
  annualSpending,
  annualIncome,
}: {
  annualSpending: MonetaryAmount | undefined | null;
  annualIncome: MonetaryAmount | undefined | null;
}) {
  if (!annualSpending || !annualIncome || annualIncome.value === 0) return undefined;

  return annualSpending.value / annualIncome.value;
}

export function hasBrDebtPaymentCorrection({
  displayAnnualSpendingSansDebtPayments,
  backendAnnualSpendingSansDebtPayments,
}: {
  displayAnnualSpendingSansDebtPayments: MonetaryAmount | undefined | null;
  backendAnnualSpendingSansDebtPayments: MonetaryAmount | undefined | null;
}) {
  return (
    !!displayAnnualSpendingSansDebtPayments &&
    !!backendAnnualSpendingSansDebtPayments &&
    displayAnnualSpendingSansDebtPayments.value < backendAnnualSpendingSansDebtPayments.value
  );
}

function getLoanDerivedAnnualDebtPayments(loans: Loan[]) {
  return addRecurringMonetaryAmounts(
    loans.map((loan) => getLoanPaymentsAndExtraPayments(loan, { frequency: RecurringFrequency.Annually })),
    RecurringFrequency.Annually
  );
}

function getGreaterMonetaryAmount(
  first: MonetaryAmount | undefined | null,
  second: MonetaryAmount | undefined | null
): MonetaryAmount | undefined | null {
  if (!first) return second;
  if (!second) return first;

  return second.value > first.value ? second : first;
}

function subtractDebtPaymentCorrection({
  estimatedAnnualSpending,
  estimatedAnnualSpendingSansDebtPayments,
  backendAnnualDebtPayments,
  totalAnnualDebtPayments,
}: {
  estimatedAnnualSpending: MonetaryAmount | undefined | null;
  estimatedAnnualSpendingSansDebtPayments: MonetaryAmount | undefined | null;
  backendAnnualDebtPayments: MonetaryAmount | undefined | null;
  totalAnnualDebtPayments: MonetaryAmount | undefined | null;
}) {
  if (!estimatedAnnualSpendingSansDebtPayments || !totalAnnualDebtPayments) {
    return estimatedAnnualSpendingSansDebtPayments;
  }

  if (estimatedAnnualSpending) {
    const correctedAnnualSpendingSansDebtPayments = {
      ...estimatedAnnualSpendingSansDebtPayments,
      value: Math.max(0, estimatedAnnualSpending.value - totalAnnualDebtPayments.value),
    };

    if (correctedAnnualSpendingSansDebtPayments.value <= estimatedAnnualSpendingSansDebtPayments.value) {
      return correctedAnnualSpendingSansDebtPayments;
    }
  }

  if (!backendAnnualDebtPayments) {
    return estimatedAnnualSpendingSansDebtPayments;
  }

  const debtPaymentCorrection = Math.max(0, totalAnnualDebtPayments.value - backendAnnualDebtPayments.value);

  return {
    ...estimatedAnnualSpendingSansDebtPayments,
    value: Math.max(0, estimatedAnnualSpendingSansDebtPayments.value - debtPaymentCorrection),
  };
}

export function useBrNumeratorDetailsPopOverData({
  householdID,
  onLoansWithDebtPaymentsFootnoteLoanClick,
}: UseBrNumeratorDetailsPopOverDataArgs): UseBrNumeratorDetailsPopOverDataReturn {
  const { brDetails, brDetailsQuery } = useBrDetails({
    householdID,
    includeAnnualExpenses: true,
    includeTotalDebtPayments: true,
    includeEstimatedSpendingMinusDebtPayments: true,
  });

  const { loans, loansQuery } = useLoans({ householdID });

  const isLoading = brDetailsQuery.isLoading || loansQuery.isLoading;

  return {
    metaData: { isLoading },
    data: getBrNumeratorDetailsPopOverDisplayData({
      brDetails,
      loans,
      onLoansWithDebtPaymentsFootnoteLoanClick,
    }),
  };
}
